import {
  Filter,
  FormColumn,
  FormResultReport,
} from 'components/registration/v2/RegistrationParticipantList';
import { Response } from 'core/api';
import { Dispatch } from 'react';
import { getData, storeRegistrationPage } from 'stores/session-storage';
import { formatFilters } from './helpers';
import { RegParticipantAction } from './reducer';
import {
  FormResultParams,
  getColumns,
  getFilters,
  getFormResults,
  getUniqueRegistrationsCount,
} from './service';
import { ActionType, LoadingType, RegParticipantState } from './types';

export interface RegParticipantsActions {
  getFormResults: (
    orgId: number,
    formId: number,
    params: FormResultParams
  ) => Promise<FormResultReport>;
  getColumns: (orgId: number, formId: number) => Promise<void>;
  getFilters: (orgId: number, formId: number) => Promise<void>;
  getUniqueRegistrationsCount: (
    orgId: number,
    formId: number,
    searchTerm: string,
    filters: object
  ) => Promise<number>;
  onSelectColumns: (selectedColumns: string[]) => void;
  updateSearchTerm: (term: string) => void;
  updateActiveFilters: (currentFilters: Record<string, string[]>) => void;
}

export function getActions(
  state: RegParticipantState,
  dispatch: Dispatch<RegParticipantAction>
): RegParticipantsActions {
  async function makeRequest<T>(
    request: (...args: any[]) => Promise<Response<T>>,
    loadingType?: LoadingType,
    ...args: any[]
  ): Promise<Response<T>> {
    let response: Response<T>;
    if (loadingType === undefined) {
      response = await request(...args);
    } else {
      dispatch({
        type: ActionType.SetLoading,
        loading: { loadingType, isLoading: true },
      });
      response = await request(...args);
      dispatch({
        type: ActionType.SetLoading,
        loading: { loadingType, isLoading: false },
      });
    }
    return response;
  }
  return {
    getFormResults: async (orgId, formId, params) => {
      const { data: response } = await makeRequest<FormResultReport>(
        getFormResults,
        LoadingType.GetFormResults,
        orgId,
        formId,
        params
      );
      dispatch({
        type: ActionType.SetFormResults,
        payload: { formResults: response },
      });
      return response;
    },
    getColumns: async (orgId, formId) => {
      const { data: response } = await makeRequest<FormColumn[]>(
        getColumns,
        LoadingType.GetColumns,
        orgId,
        formId
      );
      dispatch({
        type: ActionType.SetColumns,
        payload: { columns: response },
      });
      // Get cached columns from session storage
      const storedFilters = getData().registration;
      // Session cache is empty, add default columns to session
      let selectedColumns: string[];
      if (storedFilters?.selectedColumns?.length) {
        selectedColumns = storedFilters.selectedColumns;
      } else {
        selectedColumns = response
          .filter((col) => col.isDefault && !col.isRequired)
          .map((col) => col.name);
        storeRegistrationPage({
          selectedColumns,
          filter: { ...state.filters, searchTerm: state.searchTerm },
        });
      }

      dispatch({
        type: ActionType.SetSelectedColumns,
        payload: { selectedColumns, fromCache: true },
      });
    },
    getFilters: async (orgId, formId) => {
      const { data: response } = await makeRequest<Filter[]>(
        getFilters,
        LoadingType.GetFilters,
        orgId,
        formId
      );
      const formattedFilters: Function[] = formatFilters(response, orgId);
      dispatch({
        type: ActionType.SetFilters,
        payload: { filters: formattedFilters },
      });
    },
    getUniqueRegistrationsCount: async (orgId, formId, searchTerm, filters) => {
      const response = await makeRequest<number>(
        getUniqueRegistrationsCount,
        LoadingType.GetUniqueRegistrants,
        orgId,
        formId,
        searchTerm,
        filters
      );
      return response.data;
    },
    onSelectColumns: (selectedColumns) => {
      const selectedFormColumns = state.columns.filter((col) =>
        selectedColumns.includes(col.name)
      );
      storeRegistrationPage({
        selectedColumns: selectedFormColumns
          .filter((col) => !col.isRequired)
          .map((col) => col.name),
        filter: { ...state.filters, searchTerm: state.searchTerm },
      });
      dispatch({
        type: ActionType.SetSelectedColumns,
        payload: {
          selectedColumns,
        },
      });
    },
    updateActiveFilters: (currentFilters) => {
      storeRegistrationPage({
        filter: { ...currentFilters, searchTerm: state.searchTerm },
        selectedColumns: state.selectedColumns,
      });
      dispatch({
        type: ActionType.SetActiveFilters,
        payload: { activeFilters: currentFilters },
      });
    },
    updateSearchTerm(searchTerm) {
      storeRegistrationPage({
        filter: { ...state.activeFilters, searchTerm },
        selectedColumns: state.selectedColumns,
      });
      dispatch({
        type: ActionType.SetSearchTerm,
        payload: { searchTerm },
      });
    },
  };
}
